import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Shield, Save, Eye, Send, RotateCcw, History, Loader2, Lock } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loading } from "@/components/common/Loading";
import { PlanFeaturesPanel } from "@/components/admin/PlanFeaturesPanel";

const CATEGORY_LABEL = { security: "Security", account: "Account", transactional: "Transactional" };
const SYSTEM_BLOCKS = ["security_alert_block", "license_key_block", "founder_license_key_block", "features_block"];

export default function AdminEmailTemplates() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html_body: "", text_body: "" });
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [versions, setVersions] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [activeField, setActiveField] = useState("html_body");

  const refs = { subject: useRef(null), html_body: useRef(null), text_body: useRef(null) };

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/email-templates");
      setList(data.templates);
      if (!selected && data.templates?.length) selectTemplate(data.templates[0].template_key);
    } catch {
      toast.error("Could not load email templates.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);

  async function selectTemplate(key) {
    setSelected(key);
    setLoadingDetail(true);
    setPreview(null);
    try {
      const { data } = await api.get(`/admin/email-templates/${key}`);
      setDetail(data);
      setDraft({ subject: data.subject, html_body: data.html_body, text_body: data.text_body });
    } catch {
      toast.error("Could not load template.");
    } finally {
      setLoadingDetail(false);
    }
  }

  const dirty = useMemo(() => detail && (
    draft.subject !== detail.subject || draft.html_body !== detail.html_body || draft.text_body !== detail.text_body
  ), [draft, detail]);

  function insertPlaceholder(name) {
    const token = `{{${name}}}`;
    const el = refs[activeField]?.current;
    const cur = draft[activeField] || "";
    if (!el) { setDraft((d) => ({ ...d, [activeField]: cur + token })); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setDraft((d) => ({ ...d, [activeField]: next }));
    requestAnimationFrame(() => { el.focus(); const p = start + token.length; el.setSelectionRange?.(p, p); });
  }

  async function doSave() {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${selected}`, draft);
      toast.success(`Saved (v${data.version}).`);
      await selectTemplate(selected);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template.");
    } finally {
      setSaving(false);
    }
  }

  async function doPreview() {
    setBusy("preview");
    try {
      const { data } = await api.post(`/admin/email-templates/${selected}/preview`, draft);
      setPreview(data);
      if (data.warning) toast.warning(data.warning);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally { setBusy(""); }
  }

  async function doTestSend() {
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/email-templates/${selected}/test-send`, draft);
      if (data.delivered) {
        toast.success(`Test email sent to ${data.sent_to} (sample data only).`);
      } else if (data.mode === "log") {
        toast.warning("Recorded but NOT delivered — this environment has no email provider key. Try from production.", { duration: 9000 });
      } else {
        toast.error(`The email provider rejected this send${data.error ? ` (${data.error})` : ""}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed.");
    } finally { setBusy(""); }
  }

  async function doRestore() {
    setConfirmRestore(false);
    setBusy("restore");
    try {
      await api.post(`/admin/email-templates/${selected}/restore`);
      toast.success("Restored default copy.");
      await selectTemplate(selected);
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    } finally { setBusy(""); }
  }

  async function openVersions() {
    setBusy("versions");
    try {
      const { data } = await api.get(`/admin/email-templates/${selected}/versions`);
      setVersions(data.versions || []);
    } catch { toast.error("Could not load history."); }
    finally { setBusy(""); }
  }

  if (list === null) return <Loading />;

  return (
    <div data-testid="admin-email-templates">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-primary"><Mail className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-[680] tracking-[-0.01em] text-foreground">Email Templates</h1>
          <p className="text-sm text-muted-foreground">Edit transactional & account email copy. Sensitive values are inserted only at send time.</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* List */}
        <div className="space-y-1.5" data-testid="email-template-list">
          {list.map((t) => (
            <button
              key={t.template_key}
              data-testid={`email-template-item-${t.template_key}`}
              onClick={() => selectTemplate(t.template_key)}
              className={`w-full rounded-lg border px-3 py-2.5 text-left transition-colors ${
                selected === t.template_key ? "border-teal-300 bg-teal-50" : "border-border hover:bg-secondary"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-[560] text-foreground">{t.name}</span>
                {t.is_security_sensitive && <Shield className="h-3.5 w-3.5 shrink-0 text-amber-600" />}
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[10px]">{CATEGORY_LABEL[t.category] || t.category}</Badge>
                {t.is_overridden && <Badge className="bg-teal-600 text-[10px] hover:bg-teal-600">Edited</Badge>}
              </div>
            </button>
          ))}
        </div>

        {/* Editor */}
        <div>
          {loadingDetail || !detail ? <Loading /> : (
            <Card className="p-5" data-testid="email-template-editor">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-[680] text-foreground">{detail.name}</h2>
                  <p className="text-sm text-muted-foreground">{detail.description}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={openVersions} data-testid="email-template-history-btn" disabled={busy === "versions"}><History className="mr-1 h-3.5 w-3.5" /> History</Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirmRestore(true)} data-testid="email-template-restore-btn" disabled={!detail.is_overridden || busy === "restore"}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore default</Button>
                </div>
              </div>

              {detail.is_security_sensitive && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="email-template-security-warning">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>This template controls a <strong>security-sensitive</strong> email. Required placeholders and safety language cannot be removed. Real tokens, codes, links and license keys are inserted only at send time and are never shown here.</span>
                </div>
              )}

              <Separator className="my-4" />

              {/* Placeholder chips */}
              <div className="mb-4">
                <Label className="text-xs text-muted-foreground">Insert placeholder (into the last-focused field)</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="email-template-placeholders">
                  {detail.allowed_placeholders.map((p) => {
                    const required = detail.required_placeholders.includes(p);
                    const system = SYSTEM_BLOCKS.includes(p);
                    return (
                      <button
                        key={p}
                        onClick={() => insertPlaceholder(p)}
                        data-testid={`email-placeholder-${p}`}
                        title={system ? "System-generated block — value inserted safely at send time" : (detail.sample_values?.[p] != null ? `Sample: ${detail.sample_values[p]}` : "")}
                        className={`rounded-md border px-2 py-1 font-mono text-[11px] transition-colors ${
                          required ? "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
                          : system ? "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                          : "border-border bg-secondary text-foreground/70 hover:bg-secondary/70"
                        }`}
                      >
                        {`{{${p}}}`}{required ? " *" : ""}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  <span className="text-rose-600">*</span> required · <span className="text-violet-600">violet</span> = system-generated (keys / safety blocks, never editable text)
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="et-subject">Subject</Label>
                  <Input id="et-subject" ref={refs.subject} value={draft.subject}
                    onFocus={() => setActiveField("subject")}
                    onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                    data-testid="email-template-subject" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="et-html">HTML body</Label>
                  <Textarea id="et-html" ref={refs.html_body} value={draft.html_body} rows={12}
                    onFocus={() => setActiveField("html_body")}
                    onChange={(e) => setDraft((d) => ({ ...d, html_body: e.target.value }))}
                    className="font-mono text-xs" data-testid="email-template-html" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="et-text">Plain-text body</Label>
                  <Textarea id="et-text" ref={refs.text_body} value={draft.text_body} rows={7}
                    onFocus={() => setActiveField("text_body")}
                    onChange={(e) => setDraft((d) => ({ ...d, text_body: e.target.value }))}
                    className="font-mono text-xs" data-testid="email-template-text" />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <Button onClick={doSave} disabled={saving || !dirty} data-testid="email-template-save-btn">
                  {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
                </Button>
                <Button variant="secondary" onClick={doPreview} disabled={busy === "preview"} data-testid="email-template-preview-btn">
                  {busy === "preview" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Eye className="mr-1 h-4 w-4" />} Preview
                </Button>
                <Button variant="outline" onClick={doTestSend} disabled={busy === "test"} data-testid="email-template-testsend-btn">
                  {busy === "test" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Send test to me
                </Button>
                {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
              </div>

              {preview && (
                <div className="mt-5" data-testid="email-template-preview">
                  <Separator className="mb-4" />
                  <p className="mb-2 text-sm font-[560] text-foreground">Preview <span className="text-xs font-normal text-muted-foreground">(fake sample data — no real tokens)</span></p>
                  <p className="mb-2 text-sm"><span className="text-muted-foreground">Subject:</span> {preview.subject}</p>
                  <Tabs defaultValue="html">
                    <TabsList>
                      <TabsTrigger value="html" data-testid="preview-tab-html">HTML</TabsTrigger>
                      <TabsTrigger value="text" data-testid="preview-tab-text">Text</TabsTrigger>
                    </TabsList>
                    <TabsContent value="html">
                      <iframe title="email-preview" sandbox="" srcDoc={preview.html_body}
                        className="h-[460px] w-full rounded-lg border border-border bg-white" data-testid="email-preview-iframe" />
                    </TabsContent>
                    <TabsContent value="text">
                      <pre className="max-h-[460px] overflow-auto rounded-lg border border-border bg-secondary/40 p-3 text-xs whitespace-pre-wrap">{preview.text_body}</pre>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      <PlanFeaturesPanel />

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent data-testid="email-template-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore default copy?</AlertDialogTitle>
            <AlertDialogDescription>This discards your custom copy for "{detail?.name}" and reverts to the built-in default. The current version is saved to history first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doRestore} data-testid="email-template-restore-confirm">Restore default</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={versions !== null} onOpenChange={(o) => !o && setVersions(null)}>
        <DialogContent className="max-w-lg" data-testid="email-template-versions-dialog">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>Previous saved copies for "{detail?.name}". Copy only — no runtime secrets are stored.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] space-y-2 overflow-auto">
            {versions && versions.length === 0 && <p className="text-sm text-muted-foreground">No previous versions yet.</p>}
            {versions && versions.map((v) => (
              <div key={v.id} className="rounded-lg border border-border p-3 text-sm" data-testid={`email-template-version-${v.version}`}>
                <div className="flex items-center justify-between">
                  <span className="font-[560]">Version {v.version}</span>
                  <span className="text-xs text-muted-foreground">{v.archived_at ? new Date(v.archived_at).toLocaleString() : ""}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Subject: {v.subject}</p>
                <p className="text-xs text-muted-foreground">By: {v.updated_by?.email || "—"}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
